import type {
  SpeakerAttributionStatus,
  TranscriptSegment,
} from 'src/meetings/detail/tabs/transcript/types';
import type { CalendarEvent } from 'src/meetings/models';

// The backend's merge pipeline renames every unresolved diarisation label to a
// friendly "Unknown speaker N" (see UnknownSpeakerPresentationHeuristic), so
// that is the shape the UI actually receives. Raw "SPEAKER_n" is matched too as
// a safety net in case a transcript slips through without the presentation step.
const UNIDENTIFIED_SPEAKER_PATTERN = /^(SPEAKER_\d+|Unknown speaker \d+)$/i;

type UnidentifiedSpeaker = {
  label: string;
  // The label to send when correcting this speaker: the captured diarisation
  // label where present, so the backend matches on the original diarised speaker
  // rather than the display name. Falls back to `label` for older transcripts.
  correctionLabel: string;
  statementCount: number;
};

const isUnidentifiedSpeaker = (speaker: string): boolean =>
  UNIDENTIFIED_SPEAKER_PATTERN.test(speaker.trim());

// A speaker moves unidentified -> identified -> confirmed. "identified" is a named
// speaker the pipeline produced on its own; "confirmed" is one an adviser said is
// right. The distinction comes from the transcript's per-voice attribution
// statuses, so it only exists while the speaker pipeline is on.
export type SpeakerStatus = 'unidentified' | 'identified' | 'confirmed';

export type MeetingSpeaker = {
  // The raw `segment.speaker` value, used to key segments and corrections.
  label: string;
  // The name shown in the UI: a friendly "Speaker N" while unidentified, or the
  // resolved participant name once identified.
  displayName: string;
  // Every diarisation label grouped under this display label. Usually one, but an
  // adviser can identify several unknown speakers as the same person, so all of
  // their labels must be corrected together rather than only the first seen.
  correctionLabels: string[];
  statementCount: number;
  status: SpeakerStatus;
};

/**
 * Every distinct speaker in the transcript, in order of first appearance, with a
 * derived status: still-unidentified placeholders versus speakers already
 * resolved to a named participant. Backing data for the speaker review screen,
 * which lists everyone rather than only the ones left to identify.
 */
export const getSpeakers = (
  segments: TranscriptSegment[] | undefined,
  speakerStatuses?: Record<string, SpeakerAttributionStatus>,
): MeetingSpeaker[] => {
  if (!segments) return [];

  const byLabel = new Map<string, { statementCount: number; correctionLabels: string[] }>();
  for (const segment of segments) {
    const correctionLabel = segment.diarisationLabel ?? segment.speaker;
    const existing = byLabel.get(segment.speaker);
    if (existing) {
      existing.statementCount += 1;
      if (!existing.correctionLabels.includes(correctionLabel)) {
        existing.correctionLabels.push(correctionLabel);
      }
    } else {
      byLabel.set(segment.speaker, { statementCount: 1, correctionLabels: [correctionLabel] });
    }
  }

  return [...byLabel.entries()].map(([label, { statementCount, correctionLabels }]) => {
    const status = namedSpeakerStatus(label, correctionLabels, speakerStatuses);
    return {
      label,
      displayName: status === 'unidentified' ? formatSpeakerLabel(label) : label,
      correctionLabels,
      statementCount,
      status,
    };
  });
};

/**
 * A named speaker is only `identified` where the pipeline says so: a voice it
 * named on its own is `committed`, not `confirmed`. Without a status for any of
 * the speaker's voices — the pipeline off, or a transcript from before it — a
 * name means done, exactly as it did before.
 */
const namedSpeakerStatus = (
  label: string,
  correctionLabels: string[],
  speakerStatuses: Record<string, SpeakerAttributionStatus> | undefined,
): SpeakerStatus => {
  if (isUnidentifiedSpeaker(label)) return 'unidentified';
  const known = correctionLabels
    .map(correctionLabel => speakerStatuses?.[correctionLabel])
    .filter((status): status is SpeakerAttributionStatus => status !== undefined);
  if (known.length === 0) return 'confirmed';
  return known.every(status => status === 'confirmed') ? 'confirmed' : 'identified';
};

/**
 * A diarised transcript labels each segment with a placeholder speaker label
 * ("Unknown speaker 1", or a raw "SPEAKER_0") until someone is identified. This
 * narrows {@link getSpeakers} to the still-unidentified labels so the grouping
 * and counting logic lives in one place.
 */
export const getUnidentifiedSpeakers = (
  segments: TranscriptSegment[] | undefined,
): UnidentifiedSpeaker[] =>
  getSpeakers(segments)
    .filter(speaker => speaker.status === 'unidentified')
    .map(({ label, correctionLabels, statementCount }) => ({
      label,
      correctionLabel: correctionLabels[0] ?? label,
      statementCount,
    }));

/**
 * A speaker the pipeline named itself is outstanding work only while confirmation is on;
 * with it off a name means done, exactly as it did before. The transcript button, the
 * review screen and the meeting cards all have to agree on that, so they read the rule
 * here rather than each repeating the flag check against `identified`.
 */
export const effectiveSpeakerStatus = (
  status: SpeakerStatus,
  confirmationEnabled: boolean,
): SpeakerStatus => (status === 'identified' && !confirmationEnabled ? 'confirmed' : status);

type SpeakerReviewPrompt = {
  count: number;
  // Completes "N speakers ...", e.g. "unidentified" or "awaiting confirmation".
  state: string;
  // The link text that follows that sentence.
  action: string;
  // Closes the sentence after the link, saying what reviewing buys.
  reason: string;
  // The speaker the link should open.
  speakerLabel: string;
};

/**
 * The nudge a meeting list shows about a recording's speakers, or none where nothing is
 * outstanding. Speakers nobody has named come first, since they cost the note the most.
 * Speakers the pipeline named itself follow, and only while confirmation is on — see
 * {@link effectiveSpeakerStatus}.
 *
 * A count without a label prompts for nothing: the nudge exists to open that speaker, and
 * the backend derives both from one aggregated row, so a count means a label.
 */
export const speakerReviewPrompt = (
  event: Pick<
    CalendarEvent,
    | 'unidentifiedSpeakerCount'
    | 'firstUnidentifiedSpeakerLabel'
    | 'awaitingConfirmationCount'
    | 'firstAwaitingConfirmationLabel'
  >,
  confirmationEnabled: boolean,
): SpeakerReviewPrompt | undefined => {
  if (event.unidentifiedSpeakerCount > 0 && event.firstUnidentifiedSpeakerLabel) {
    return {
      count: event.unidentifiedSpeakerCount,
      state: 'unidentified',
      action: 'Identify speakers',
      reason: 'for a more accurate note.',
      speakerLabel: event.firstUnidentifiedSpeakerLabel,
    };
  }
  const identifiedIsOutstanding =
    effectiveSpeakerStatus('identified', confirmationEnabled) === 'identified';
  if (
    identifiedIsOutstanding &&
    event.awaitingConfirmationCount > 0 &&
    event.firstAwaitingConfirmationLabel
  ) {
    return {
      count: event.awaitingConfirmationCount,
      state: 'awaiting confirmation',
      action: 'Review speakers',
      // Agreeing with a name the pipeline got right leaves the note untouched — only the
      // attribution becomes human — so the promise here is catching a wrong name, not a
      // better note.
      reason: 'to check the names are right.',
      speakerLabel: event.firstAwaitingConfirmationLabel,
    };
  }
  return undefined;
};

/**
 * Turns a placeholder diarisation label into a friendlier display name, e.g.
 * "SPEAKER_0" becomes "Speaker 1" and "Unknown speaker 2" becomes "Speaker 2".
 * Anything else is shown as-is.
 */
export const formatSpeakerLabel = (label: string): string => {
  const rawSpeakerMatch = label.match(/^SPEAKER_(\d+)$/);
  if (rawSpeakerMatch) return `Speaker ${Number(rawSpeakerMatch[1]) + 1}`;

  const unknownSpeakerMatch = label.match(/^Unknown speaker (\d+)$/i);
  if (unknownSpeakerMatch) return `Speaker ${Number(unknownSpeakerMatch[1])}`;

  return label;
};
